// Service package: the rules with no screen (Today view, suggestions, smart defaults, board filters, day route). Plain JS: browser, server, tests.
export const STATUSES = ['Draft', 'Estimate', 'Approved', 'Scheduled', 'In progress', 'Done', 'Invoiced']
export const OPEN = new Set(['Draft', 'Estimate', 'Approved', 'Scheduled', 'In progress'])
export const STATUS_TONE = { Draft: '#64748b', Estimate: '#b7791f', Approved: '#0e7490', Scheduled: '#1B4FD8', 'In progress': '#7c3aed', Done: '#137050', Invoiced: '#334155' }
export const REMIND_AFTER_DAYS = 2
const DAY = 864e5
export const REQUEST_SOURCES = new Set(['crew', 'owner', 'request'])
export const URGENCY = [['normal', 'Can wait'], ['soon', 'Soon'], ['urgent', 'Urgent']]
const URGENCY_RANK = { urgent: 0, soon: 1, normal: 2 }
export const urgencyLabel = u => (URGENCY.find(x => x[0] === u) || [null, null])[1]
export const isoDay = d => new Date(d).toISOString().slice(0, 10)
export const addDays = (iso, n) => isoDay(Date.parse(String(iso).slice(0, 10) + 'T12:00:00Z') + n * DAY)
export const weekdayOf = iso => new Date(Date.parse(String(iso).slice(0, 10) + 'T12:00:00Z')).getUTCDay()
export const mondayOf = iso => addDays(iso, -((weekdayOf(iso) + 6) % 7))
export const weekOf = iso => { const m = mondayOf(iso); return [0, 1, 2, 3, 4, 5, 6].map(i => addDays(m, i)) }
export const nextWorkday = iso => { let d = addDays(iso, 1); while ([0, 6].includes(weekdayOf(d))) d = addDays(d, 1); return d }
export const jobRef = j => 'Job ' + (j && j.job_no != null ? j.job_no : '')
export const money = (n, cur = 'USD') => { try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(Number(n) || 0) } catch (e) { return (Number(n) || 0).toFixed(2) + ' ' + cur } }
export const dayLabel = iso => (iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) : 'No date')

// ---------- the manager's one view ----------
// jobs: work_orders; estimates: work_order_estimates with status 'sent'
export function todayView({ jobs = [], estimates = [], today, now = Date.now() }) {
  const est = new Map(estimates.filter(e => e.status === 'sent').map(e => [e.work_order_id, e]))
  const byVessel = (a, b) => String(a.vessel_name || '').localeCompare(String(b.vessel_name || ''))
  const remindAt = e => Date.parse(e.reminded_at || e.sent_at || 0) + REMIND_AFTER_DAYS * DAY
  return {
    today: jobs.filter(j => OPEN.has(j.status) && j.scheduled_date === today).sort(byVessel),
    overdue: jobs.filter(j => ['Approved', 'Scheduled', 'In progress'].includes(j.status) && j.scheduled_date && j.scheduled_date < today).sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date)),
    waiting: jobs.filter(j => j.status === 'Estimate').map(j => { const e = est.get(j.id) || null; return { ...j, estimate: e, remind: !!(e && remindAt(e) <= now) } }),
    requests: jobs.filter(j => j.status === 'Draft' && REQUEST_SOURCES.has(j.source)).sort((a, b) => (URGENCY_RANK[(a.request || {}).urgency] ?? 2) - (URGENCY_RANK[(b.request || {}).urgency] ?? 2)),
    drafts: jobs.filter(j => j.status === 'Draft' && !REQUEST_SOURCES.has(j.source)),
  }
}

// ---------- automation: what to do next ----------
// tasks: maintenance_tasks due within `days` (or overdue) and not already on an open job -> ONE suggested job per boat per day
export function suggestJobs({ tasks = [], jobs = [], jobTasks = [], vessels = [], today, days = 14, equipment = {} }) {
  const open = new Set(jobs.filter(j => OPEN.has(j.status)).map(j => j.id))
  const taken = new Set(jobTasks.filter(t => t.maintenance_task_id && open.has(t.work_order_id)).map(t => t.maintenance_task_id))
  const name = Object.fromEntries(vessels.map(v => [v.id, v.vessel_name]))
  const last = addDays(today, days), groups = new Map()
  for (const t of tasks) {
    if (!name[t.client_id] || taken.has(t.id) || t.archived_at) continue
    const due = /^\d{4}-\d{2}-\d{2}/.test(String(t.next_service_date || '')) ? String(t.next_service_date).slice(0, 10) : null
    const late = t.status === 'Overdue' || (due && due < today)
    if (!late && !(due && due <= last) && t.status !== 'Due') continue
    const date = late || !due || due <= today ? nextWorkday(today) : due
    const key = t.client_id + '|' + date
    if (!groups.has(key)) groups.set(key, { key, kind: 'maintenance', client_id: t.client_id, vessel_name: name[t.client_id], date, late: false, lines: [] })
    const g = groups.get(key)
    g.late = g.late || !!late
    const eq = equipment[t.client_id + '|' + t.equipment_uid]
    g.lines.push({ maintenance_task_id: t.id, text: (t.task_name || 'Maintenance task') + (eq ? ' - ' + eq : '') })
  }
  return [...groups.values()].map(g => ({ ...g, title: g.lines.length === 1 ? g.lines[0].text : g.lines.length + ' maintenance tasks' })).sort((a, b) => a.date.localeCompare(b.date) || a.vessel_name.localeCompare(b.vessel_name))
}
// a group's regular visit (e.g. weekly check every Monday): the next one per vessel, unless already planned
export function suggestVisits({ groups = [], settings = [], members = [], jobs = [], vessels = [], today, days = 7 }) {
  const set = new Map(settings.map(s => [s.group_id, s])), name = Object.fromEntries(vessels.map(v => [v.id, v.vessel_name])), out = [], seen = new Set()
  for (const g of groups) {
    const s = set.get(g.id)
    if (!s || s.visit_weekday == null) continue
    let d = today
    for (let i = 0; i < 7 && weekdayOf(d) !== Number(s.visit_weekday); i++) d = addDays(d, 1)
    if (d > addDays(today, days)) continue
    const title = s.visit_title || 'Regular visit'
    for (const m of members.filter(x => x.group_id === g.id && name[x.client_id])) {
      const key = 'visit|' + m.client_id + '|' + d + '|' + title
      if (seen.has(key) || jobs.some(j => j.client_id === m.client_id && j.scheduled_date === d && j.title === title)) continue
      seen.add(key)
      out.push({ key, kind: 'visit', client_id: m.client_id, vessel_name: name[m.client_id], date: d, title, group: g.name, technician_id: s.technician_id || null, lines: [] })
    }
  }
  return out
}

// ---------- smart defaults: the last similar job; the user only corrects ----------
const WORDS = s => new Set(String(s || '').toLowerCase().match(/[a-z0-9]{3,}/g) || [])
export function similarity(a, b) {
  const x = WORDS(a), y = WORDS(b)
  if (!x.size || !y.size) return 0
  let n = 0
  for (const w of x) if (y.has(w)) n++
  return n / Math.max(x.size, y.size)
}
export function defaultsFor({ title = '', clientId, history = [], historyTasks = [], effective = {}, today }) {
  const done = history.filter(j => !title || similarity(title, j.title) >= 0.5).sort((a, b) => ((b.client_id === clientId) - (a.client_id === clientId)) || String(b.created_at).localeCompare(String(a.created_at)))
  const last = done[0] || null
  const onVessel = history.filter(j => j.client_id === clientId && j.technician_id).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0]
  return {
    technician_id: effective.technician_id || (last && last.technician_id) || (onVessel && onVessel.technician_id) || null,
    scheduled_date: nextWorkday(today),
    labour_hours: last && Number(last.labour_hours) > 0 ? Number(last.labour_hours) : 0,
    lines: last ? historyTasks.filter(t => t.work_order_id === last.id).map(t => t.text) : [],
    from: last ? jobRef(last) : null,
  }
}

// ---------- the Jobs board ----------
export function filterJobs(jobs, f = {}, members = []) {
  const inGroup = f.group ? new Set(members.filter(m => m.group_id === f.group).map(m => m.client_id)) : null
  return jobs.filter(j => (!f.vessel || j.client_id === f.vessel) && (!f.technician || (f.technician === 'none' ? !j.technician_id : j.technician_id === f.technician)) && (!f.status || j.status === f.status)
    && (!f.from || (j.scheduled_date && j.scheduled_date >= f.from)) && (!f.to || (j.scheduled_date && j.scheduled_date <= f.to)) && (!inGroup || inGroup.has(j.client_id)))
}
export const groupVessels = (groupId, members) => members.filter(m => m.group_id === groupId).map(m => m.client_id)

// ---------- the technician's day route ----------
const LIVE = ['Approved', 'Scheduled', 'In progress']
// in progress first, then late, then by vessel so one boat's jobs sit together
export function dayRoute(jobs, today) {
  const rank = j => (j.status === 'In progress' ? 0 : j.scheduled_date < today ? 1 : 2)
  const list = jobs.filter(j => LIVE.includes(j.status) && j.scheduled_date && j.scheduled_date <= today)
  return list.sort((a, b) => rank(a) - rank(b) || String(a.vessel_name).localeCompare(String(b.vessel_name)) || a.scheduled_date.localeCompare(b.scheduled_date))
}
export const laterThisWeek = (jobs, today) => jobs.filter(j => LIVE.includes(j.status) && j.scheduled_date && j.scheduled_date > today && j.scheduled_date <= addDays(today, 7)).sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || String(a.vessel_name).localeCompare(String(b.vessel_name)))
export const minutes = rows => Math.round((rows || []).reduce((n, r) => n + (r.ended_at ? Date.parse(r.ended_at) - Date.parse(r.started_at) : 0), 0) / 6e4)
export const hoursText = m => { const h = Math.floor(m / 60), r = m % 60; return (h ? h + ' h ' : '') + r + ' min' }

// ---------- weather on Today: the existing weather at the stored position, else the home port, else nothing ----------
export function weatherSource(vessel, positions = {}, homePorts = {}) {
  const p = positions[vessel.mmsi ? String(vessel.mmsi) : 'vessel:' + vessel.id]
  if (p && p.lat != null && p.lon != null) return { lat: p.lat, lon: p.lon, from: 'position' }
  const port = String(homePorts[vessel.id] || '').trim()
  return port ? { port, from: 'home port' } : null
}

// ---------- vessel setting vs group: "the vessel wins", shown clearly ----------
// own: the vessel's own value (null = none); groupValues: [{ group, value }] (alphabetical)
export function settingLine(own, groupValues = [], fallback = null) {
  const g = groupValues.find(x => x.value != null) || null
  const value = own != null ? own : g ? g.value : fallback
  return { value, from: own != null ? 'vessel' : g ? g.group : 'default', overrides: own != null && g != null && String(own) !== String(g.value) ? g.group : null, conflict: new Set(groupValues.filter(x => x.value != null).map(x => String(x.value))).size > 1 }
}

// ---------- the simplicity pass: short lists and one-line labels ----------
export const SHOW_FIRST = 5
// one line: the first line of the text, without the "Survey: " in front, cut at a word
export function shortLine(text, max = 60) {
  const t = String(text == null ? '' : text).split('\n')[0].replace(/^survey:\s*/i, '').replace(/\s+/g, ' ').trim()
  if (t.length <= max) return t
  const cut = t.slice(0, max), sp = cut.lastIndexOf(' ')
  return (sp > max * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s,;:.(-]+$/, '') + '…'
}
// what can be added to a job: overdue first, without what is already on it. full = the whole text (the line keeps it); label = one line.
export function pickGroups({ tasks = [], defects = [], lines = [] }) {
  const usedT = new Set(lines.map(l => l.maintenance_task_id).filter(Boolean)), usedD = new Set(lines.map(l => l.todo_item_id).filter(Boolean))
  const rank = t => (t.status === 'Overdue' ? 0 : 1)
  return {
    maintenance: tasks.filter(t => !usedT.has(t.id)).map((t, i) => [t, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([t]) => ({ id: t.id, label: shortLine(t.task_name), full: t.task_name, tag: t.status === 'Overdue' ? '' : 'Due', source: t })),
    defects: defects.filter(t => !usedD.has(t.id)).map(t => ({ id: t.id, label: shortLine(t.text), full: t.text, tag: /^survey:/i.test(String(t.text)) ? 'Survey' : '', source: t })),
  }
}
// most urgent first: overdue, then by date; regular visits keep their date order among the rest
export const rankSuggestions = list => list.map((s, i) => [s, i]).sort((a, b) => (a[0].late ? 0 : 1) - (b[0].late ? 0 : 1) || String(a[0].date).localeCompare(String(b[0].date)) || a[1] - b[1]).map(([s]) => s)
// a suggestion's checklist in one line
export const linesSummary = (lines, n = 2) => { const t = lines.map(l => shortLine(l.text, 40)); return t.slice(0, n).join(', ') + (t.length > n ? ' +' + (t.length - n) + ' more' : '') }

